import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleFlightsBookingLinkResponse } from '@dreamai/types';
import api from '../api';
import PublicNoticePage from './common/PublicNoticePage';
import './booking-page.scss';

const BookingPage: React.FC = () => {
  const { t } = useTranslation();
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const [bookingData, setBookingData] =
    useState<GoogleFlightsBookingLinkResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    const fetchBookingData = async () => {
      if (!key) {
        setError(t('booking.noKey'));
        setLoading(false);
        return;
      }

      try {
        // Make request without authentication since route is now public
        const response = await api.get<GoogleFlightsBookingLinkResponse>(
          `/google-flights-booking-links/${key}`,
        );

        if (response.data.bookingUrl && response.data.postData) {
          setBookingData(response.data);
          // Start 5-second countdown after data is loaded
          setCountdown(5);
        } else {
          setError(t('booking.invalidData'));
        }
      } catch (error) {
        console.error('Error fetching booking data:', error);
        setError(t('booking.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchBookingData();
  }, [key]);

  // Auto-redirect countdown effect
  useEffect(() => {
    if (countdown === null || countdown <= 0) return;

    const timer = setTimeout(() => {
      if (countdown === 1) {
        // Time's up, redirect
        handleBookingSubmit();
      } else {
        // Decrement countdown
        setCountdown(countdown - 1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, bookingData]);

  const handleBookingSubmit = () => {
    if (!bookingData) return;

    // Create a form that submits the URL-encoded POST data (same as SpecialMessageArtifacts)
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = bookingData.bookingUrl;
    form.target = '_self'; // Don't open new window, navigate in same tab
    form.style.display = 'none';
    form.enctype = 'application/x-www-form-urlencoded';

    // Parse the URL-encoded POST data and create form fields
    const params = new URLSearchParams(bookingData.postData);
    params.forEach((value, key) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  if (loading) {
    return (
      <div className='booking-page'>
        <div className='booking-container'>
          <div className='loading-spinner'>
            <div className='spinner'></div>
            <p>{t('booking.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <PublicNoticePage
        title={t('booking.errorTitle')}
        body={error}
        actions={
          <button
            type='button'
            className='public-notice__button'
            onClick={() => navigate('/')}
          >
            {t('booking.goHome')}
          </button>
        }
      />
    );
  }

  if (!bookingData) {
    return (
      <PublicNoticePage
        title={t('booking.noDataTitle')}
        body={t('booking.noDataBody')}
        actions={
          <button
            type='button'
            className='public-notice__button'
            onClick={() => navigate('/')}
          >
            {t('booking.goHome')}
          </button>
        }
      />
    );
  }

  return (
    <div className='booking-page'>
      <div className='booking-container'>
        <div className='booking-header'>
          <h1>{t('booking.title')}</h1>
          <p>{t('booking.redirectBody')}</p>
        </div>

        <div className='booking-details'>
          <div className='provider-info'>
            <h3>{t('booking.provider')}</h3>
            <p>{bookingData.bookWith || t('booking.providerFallback')}</p>
          </div>

          {countdown !== null && countdown > 0 && (
            <div className='countdown-info'>
              <p>{t('booking.redirectingIn', { seconds: countdown })}</p>
            </div>
          )}

          <div className='booking-actions'>
            <button className='book-button' onClick={handleBookingSubmit}>
              {t('booking.continueTo', {
                provider:
                  bookingData.bookWith || t('booking.bookingSiteFallback'),
              })}
            </button>
          </div>
        </div>

        <div className='booking-footer'>
          <p className='disclaimer'>{t('booking.disclaimer')}</p>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
